import React, { useMemo } from 'react';
import { useFieldArray, useFormContext, useWatch } from 'react-hook-form';
import { AgentFormValues, SectionCard, areaCls } from './agentShared';
import { classifyPrompts, globalBlockIndices, moveWithinSubset } from './promptPlacement';
import { cn } from '@/utils/cn';
import { Button } from '@/components/UI/Button';
import { EmptyState } from '@/components/UI/EmptyState';
import PromptBlock from './PromptBlock';

interface InstructionsTabProps {
  isEdit: boolean;
  onPreview: () => void;
  previewing: boolean;
}

const InstructionsTab: React.FC<InstructionsTabProps> = ({ isEdit, onPreview, previewing }) => {
  const { control, register } = useFormContext<AgentFormValues>();

  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'system_prompts',
  });

  // Only truly global blocks (no home_module, no topic_modules/condition_modules) show here —
  // module-scoped General/Specific blocks are edited from that screen's own card in the Modules
  // tab (see promptPlacement.ts, the single source of truth shared with ScreensTab.tsx).
  //
  // Classify from useWatch, NOT `fields` — useFieldArray's `fields` only reflects STRUCTURAL
  // changes (append/remove/move); a nested VALUE change via setValue (e.g. a Modules-tab Convert,
  // or this tab's own category/role pickers) does not update `fields` until some other structural
  // change forces a re-render, which would show a block under the wrong classification in the
  // meantime. `fields` is still fine (and intended) for the stable `.id` React key below.
  const watchedSystemPrompts = useWatch({ control, name: 'system_prompts' });
  const placements = useMemo(() => classifyPrompts(watchedSystemPrompts ?? []), [watchedSystemPrompts]);
  const visible = useMemo(() => globalBlockIndices(placements), [placements]);
  const hiddenCount = fields.length - visible.length;

  return (
    <>
      {/* Audio prompt — prepended to the composed system prompt on voice turns only
          (realtime + push-to-talk). Its own field, not a system_prompts block, so it always sits
          above the whole composed prompt. Left blank → nothing is prepended. */}
      <SectionCard
        title="Audio prompt"
        desc="Prepended to the system prompt on voice turns only (realtime + push-to-talk). Use this for spoken-reply rules: brevity, no re-greeting, no filler openers. Leave blank to inject nothing."
      >
        <textarea
          rows={6}
          placeholder="e.g. Reply as natural, casual spoken conversation. One short sentence default. Never re-greet after the first turn. No filler openers (nice / great / got it)."
          className={cn(areaCls, 'font-mono text-sm leading-relaxed')}
          {...register('audio_prompt')}
        />
      </SectionCard>

      {/* System prompts */}
      <SectionCard
        title="System prompts"
        desc="Each block is injected in order. Tag blocks by purpose and add conditions to target specific roles or contexts."
        right={
          isEdit ? (
            <Button type="button" variant="soft" size="xs" onClick={onPreview} disabled={previewing}>
              {previewing ? 'Loading…' : 'Preview compiled prompt'}
            </Button>
          ) : undefined
        }
      >
        {/* Legend */}
        <div className="mb-4 flex flex-wrap gap-x-4 gap-y-1.5">
          {[
            { label: 'Persona',  dot: 'bg-accent',  desc: 'Identity & tone' },
            { label: 'Rules',    dot: 'bg-danger',     desc: 'Guardrails' },
            { label: 'Context',  dot: 'bg-accent',    desc: 'Domain knowledge' },
            { label: 'Format',   dot: 'bg-success', desc: 'Response style' },
            { label: 'Tools',    dot: 'bg-warning',   desc: 'Tool usage' },
          ].map((c) => (
            <span key={c.label} className="inline-flex items-center gap-1.5 text-xs text-ui-text3">
              <span className={`h-2 w-2 rounded-full ${c.dot}`} />
              <span className="font-medium text-ui-text2">{c.label}</span>
              <span>— {c.desc}</span>
            </span>
          ))}
        </div>

        <div className="space-y-3">
          {visible.length === 0 && (
            <EmptyState variant="inline" title="No global prompt blocks yet. Module-specific prompts live in the Modules tab." />
          )}

          {visible.map((idx, pos) => (
            <PromptBlock
              key={fields[idx].id}
              index={idx}
              total={visible.length}
              onMove={(dir) => {
                const pair = moveWithinSubset(visible, pos, dir);
                if (pair) move(pair[0], pair[1]);
              }}
              onRemove={() => remove(idx)}
            />
          ))}

          <Button type="button" variant="soft" size="sm" onClick={() => append({ title: '', prompt: '' })}>
            + Add prompt block
          </Button>

          {hiddenCount > 0 && (
            <p className="text-xs text-ui-text3">
              {hiddenCount} module-scoped or legacy prompt block{hiddenCount === 1 ? '' : 's'} hidden here — manage {hiddenCount === 1 ? 'it' : 'them'} from the Modules tab.
            </p>
          )}
        </div>
      </SectionCard>

    </>
  );
};

export default InstructionsTab;
